import { useRef, useState, useEffect, useCallback } from 'react';
import type { ZoneMeasurementType } from '../../types';

interface ZoneData {
  id?: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  measurement_type?: ZoneMeasurementType;
}

interface ZoneCanvasProps {
  snapshotUrl: string | null;
  zones: ZoneData[];
  selectedZoneId: string | null;
  zoneCounts?: Map<string, number>; // zone_id -> current count
  onZoneCreate: (zone: { x: number; y: number; width: number; height: number }) => void;
  onZoneUpdate: (id: string, updates: Partial<ZoneData>) => void;
  onZoneSelect: (id: string | null) => void;
  onZoneDelete: (id: string) => void;
}

type InteractionMode = 'idle' | 'drawing' | 'moving' | 'resizing';
type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | null;

const HANDLE_SIZE = 10;
const COLORS = {
  item_count: { fill: 'rgba(59, 130, 246, 0.2)', stroke: '#3B82F6' },
  person_dwell: { fill: 'rgba(168, 85, 247, 0.2)', stroke: '#A855F7' },
  default: { fill: 'rgba(34, 197, 94, 0.2)', stroke: '#22C55E' },
  selected: { fill: 'rgba(249, 115, 22, 0.3)', stroke: '#F97316' },
};

export default function ZoneCanvas({
  snapshotUrl,
  zones,
  selectedZoneId,
  zoneCounts,
  onZoneCreate,
  onZoneUpdate,
  onZoneSelect,
  onZoneDelete,
}: ZoneCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const [mode, setMode] = useState<InteractionMode>('idle');
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [drawEnd, setDrawEnd] = useState<{ x: number; y: number } | null>(null);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 450 });

  // Load image and set canvas size
  useEffect(() => {
    if (!snapshotUrl) return;

    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      // Maintain aspect ratio
      const container = containerRef.current;
      if (container) {
        const maxWidth = container.clientWidth;
        const aspectRatio = img.height / img.width;
        const width = Math.min(maxWidth, img.width);
        const height = width * aspectRatio;
        setCanvasSize({ width, height });
      }
    };
    img.src = snapshotUrl;
  }, [snapshotUrl]);

  // Convert pixel coordinates to normalized (0-1)
  const pixelToNormalized = useCallback((x: number, y: number): [number, number] => {
    return [x / canvasSize.width, y / canvasSize.height];
  }, [canvasSize]);

  // Convert normalized coordinates to pixels
  const normalizedToPixel = useCallback((x: number, y: number): [number, number] => {
    return [x * canvasSize.width, y * canvasSize.height];
  }, [canvasSize]);

  // Get mouse position relative to canvas
  const getMousePos = useCallback((e: React.MouseEvent): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  // Check if point is inside a zone
  const hitTestZone = useCallback((x: number, y: number): string | null => {
    const [nx, ny] = pixelToNormalized(x, y);
    for (let i = zones.length - 1; i >= 0; i--) {
      const zone = zones[i];
      if (!('id' in zone) || !zone.id) continue;
      if (
        nx >= zone.x &&
        nx <= zone.x + zone.width &&
        ny >= zone.y &&
        ny <= zone.y + zone.height
      ) {
        return zone.id;
      }
    }
    return null;
  }, [zones, pixelToNormalized]);

  // Check if point is on a resize handle
  const hitTestHandle = useCallback((x: number, y: number, zoneId: string): ResizeHandle => {
    const zone = zones.find((z) => 'id' in z && z.id === zoneId);
    if (!zone) return null;

    const [zx, zy] = normalizedToPixel(zone.x, zone.y);
    const [zw, zh] = normalizedToPixel(zone.width, zone.height);

    const handles: { handle: ResizeHandle; x: number; y: number }[] = [
      { handle: 'nw', x: zx, y: zy },
      { handle: 'ne', x: zx + zw, y: zy },
      { handle: 'sw', x: zx, y: zy + zh },
      { handle: 'se', x: zx + zw, y: zy + zh },
    ];

    for (const h of handles) {
      if (Math.abs(x - h.x) <= HANDLE_SIZE && Math.abs(y - h.y) <= HANDLE_SIZE) {
        return h.handle;
      }
    }
    return null;
  }, [zones, normalizedToPixel]);

  // Draw everything
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
    } else {
      // Placeholder background
      ctx.fillStyle = '#1F2937';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#6B7280';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('No snapshot available', canvas.width / 2, canvas.height / 2);
    }

    // Draw zones
    zones.forEach((zone) => {
      const id = 'id' in zone ? zone.id : null;
      const isSelected = id === selectedZoneId;
      const measurementType = 'measurement_type' in zone ? zone.measurement_type : 'item_count';
      const colorScheme = isSelected ? COLORS.selected : (COLORS[measurementType as keyof typeof COLORS] || COLORS.default);

      const [x, y] = normalizedToPixel(zone.x, zone.y);
      const [w, h] = normalizedToPixel(zone.width, zone.height);

      // Fill
      ctx.fillStyle = colorScheme.fill;
      ctx.fillRect(x, y, w, h);

      // Border
      ctx.strokeStyle = colorScheme.stroke;
      ctx.lineWidth = isSelected ? 3 : 2;
      ctx.setLineDash(isSelected ? [] : [5, 5]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);

      // Label with count
      const name = 'name' in zone ? zone.name : 'New Zone';
      if (name) {
        const count = id && zoneCounts ? zoneCounts.get(id) : undefined;
        const labelText = count !== undefined ? `${name}: ${count}` : name;

        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'left';
        const textWidth = ctx.measureText(labelText).width;

        // Draw label background
        ctx.fillStyle = colorScheme.stroke;
        ctx.fillRect(x, y - 2, textWidth + 10, 20);

        // Draw label text
        ctx.fillStyle = 'white';
        ctx.fillText(labelText, x + 5, y + 12);
      }

      // Resize handles for selected zone
      if (isSelected && id) {
        ctx.fillStyle = colorScheme.stroke;
        const handles = [
          { x: x, y: y },
          { x: x + w, y: y },
          { x: x, y: y + h },
          { x: x + w, y: y + h },
        ];
        handles.forEach((h) => {
          ctx.fillRect(h.x - HANDLE_SIZE / 2, h.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
        });
      }
    });

    // Draw in-progress rectangle
    if (mode === 'drawing' && drawStart && drawEnd) {
      const x = Math.min(drawStart.x, drawEnd.x);
      const y = Math.min(drawStart.y, drawEnd.y);
      const w = Math.abs(drawEnd.x - drawStart.x);
      const h = Math.abs(drawEnd.y - drawStart.y);

      ctx.fillStyle = COLORS.default.fill;
      ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = COLORS.default.stroke;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
    }
  }, [zones, selectedZoneId, zoneCounts, mode, drawStart, drawEnd, normalizedToPixel]);

  // Redraw on state changes
  useEffect(() => {
    draw();
  }, [draw]);

  // Handle mouse down
  const handleMouseDown = (e: React.MouseEvent) => {
    const pos = getMousePos(e);

    // Check for resize handle first
    if (selectedZoneId) {
      const handle = hitTestHandle(pos.x, pos.y, selectedZoneId);
      if (handle) {
        setMode('resizing');
        setResizeHandle(handle);
        return;
      }
    }

    // Check for zone click
    const hitZone = hitTestZone(pos.x, pos.y);
    if (hitZone) {
      onZoneSelect(hitZone);
      setMode('moving');
      const zone = zones.find((z) => 'id' in z && z.id === hitZone);
      if (zone) {
        const [zx, zy] = normalizedToPixel(zone.x, zone.y);
        setDragOffset({ x: pos.x - zx, y: pos.y - zy });
      }
      return;
    }

    // Start drawing new zone
    onZoneSelect(null);
    setMode('drawing');
    setDrawStart(pos);
    setDrawEnd(pos);
  };

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent) => {
    const pos = getMousePos(e);

    if (mode === 'drawing' && drawStart) {
      setDrawEnd(pos);
    } else if (mode === 'moving' && selectedZoneId && dragOffset) {
      const [nx, ny] = pixelToNormalized(pos.x - dragOffset.x, pos.y - dragOffset.y);
      // Clamp to canvas bounds
      const zone = zones.find((z) => 'id' in z && z.id === selectedZoneId);
      if (zone) {
        const x = Math.max(0, Math.min(1 - zone.width, nx));
        const y = Math.max(0, Math.min(1 - zone.height, ny));
        onZoneUpdate(selectedZoneId, { x, y });
      }
    } else if (mode === 'resizing' && selectedZoneId && resizeHandle) {
      const zone = zones.find((z) => 'id' in z && z.id === selectedZoneId);
      if (zone) {
        const [nx, ny] = pixelToNormalized(pos.x, pos.y);
        let newX = zone.x;
        let newY = zone.y;
        let newWidth = zone.width;
        let newHeight = zone.height;

        if (resizeHandle.includes('w')) {
          newWidth = zone.x + zone.width - nx;
          newX = nx;
        } else if (resizeHandle.includes('e')) {
          newWidth = nx - zone.x;
        }

        if (resizeHandle.includes('n')) {
          newHeight = zone.y + zone.height - ny;
          newY = ny;
        } else if (resizeHandle.includes('s')) {
          newHeight = ny - zone.y;
        }

        // Ensure minimum size and bounds
        if (newWidth > 0.02 && newHeight > 0.02 && newX >= 0 && newY >= 0 && newX + newWidth <= 1 && newY + newHeight <= 1) {
          onZoneUpdate(selectedZoneId, {
            x: Math.round(newX * 10000) / 10000,
            y: Math.round(newY * 10000) / 10000,
            width: Math.round(newWidth * 10000) / 10000,
            height: Math.round(newHeight * 10000) / 10000,
          });
        }
      }
    }

    // Update cursor
    const canvas = canvasRef.current;
    if (canvas) {
      if (selectedZoneId && hitTestHandle(pos.x, pos.y, selectedZoneId)) {
        canvas.style.cursor = 'nwse-resize';
      } else if (hitTestZone(pos.x, pos.y)) {
        canvas.style.cursor = 'move';
      } else {
        canvas.style.cursor = 'crosshair';
      }
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    if (mode === 'drawing' && drawStart && drawEnd) {
      const x = Math.min(drawStart.x, drawEnd.x);
      const y = Math.min(drawStart.y, drawEnd.y);
      const w = Math.abs(drawEnd.x - drawStart.x);
      const h = Math.abs(drawEnd.y - drawStart.y);

      // Only create if zone is large enough
      if (w > 20 && h > 20) {
        const [nx, ny] = pixelToNormalized(x, y);
        const [nw, nh] = pixelToNormalized(w, h);
        onZoneCreate({
          x: Math.round(nx * 10000) / 10000,
          y: Math.round(ny * 10000) / 10000,
          width: Math.round(nw * 10000) / 10000,
          height: Math.round(nh * 10000) / 10000,
        });
      }
    }

    setMode('idle');
    setDrawStart(null);
    setDrawEnd(null);
    setResizeHandle(null);
    setDragOffset(null);
  };

  // Handle keyboard
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedZoneId) {
          onZoneDelete(selectedZoneId);
        }
      } else if (e.key === 'Escape') {
        onZoneSelect(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedZoneId, onZoneDelete, onZoneSelect]);

  return (
    <div ref={containerRef} className="w-full">
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        className="border border-gray-300 rounded-lg"
        style={{ cursor: 'crosshair' }}
      />
      <p className="text-xs text-gray-500 mt-2">
        Click and drag to draw zones. Click a zone to select it. Press Delete to remove selected zone.
      </p>
    </div>
  );
}
